import type { ListenerEffectAPI, ThunkDispatch, UnknownAction } from '@reduxjs/toolkit';
import { createAction, createListenerMiddleware } from '@reduxjs/toolkit';

import { type CapabilitiesState, selectCapability } from '../capabilities-slice';
import { addNotification } from '../notifications-slice';
import type { ConfigurationState } from './configuration-slice';
import { appUpdateNotificationShown, fetchAppManifest } from './configuration-slice';

export const startAppUpdateChecks = createAction('configuration/startAppUpdateChecks');

export function createConfigurationListenerMiddleware<
  State extends {
    capabilities: CapabilitiesState;
    configuration: ConfigurationState;
  },
  Dispatch extends ThunkDispatch<State, unknown, UnknownAction>,
>() {
  let appUpdateTimer: number | undefined = undefined;

  function cancelAppUpdateProbe() {
    if (appUpdateTimer !== undefined) {
      window.clearTimeout(appUpdateTimer);
    }
  }

  function startAppUpdateProbe(listenerApi: ListenerEffectAPI<State, Dispatch>) {
    // daily check for app updates
    const CHECK_INTERVAL = 1000 * 60 * 60 * 24;

    cancelAppUpdateProbe();

    void listenerApi.dispatch(fetchAppManifest());

    appUpdateTimer = window.setTimeout(() => {
      void startAppUpdateProbe(listenerApi);
    }, CHECK_INTERVAL);
  }

  const middleware = createListenerMiddleware<State, Dispatch>();

  middleware.startListening({
    predicate(action, currentState) {
      const appUpdateChecksSupported = selectCapability(currentState, { key: 'framework:app-update-checks' });
      const appUpdateChecksEnabled = startAppUpdateChecks.match(action);
      return appUpdateChecksSupported && appUpdateChecksEnabled;
    },
    effect(_, listenerApi) {
      startAppUpdateProbe(listenerApi);
    },
  });

  // Prompt the user to refresh the page when a new version of the application is available
  middleware.startListening({
    predicate(action, currentState, previousState) {
      return (
        currentState.configuration.remoteAppManifest.version !== previousState.configuration.remoteAppManifest.version
      );
    },
    effect(action, listenerApi) {
      const { appManifest, remoteAppManifest, appUpdateNotificationShownTimestamp } =
        listenerApi.getState().configuration;

      const newVersionAvailable =
        appManifest.version !== undefined &&
        remoteAppManifest.version !== undefined &&
        remoteAppManifest.version !== appManifest.version;

      const MS_PER_DAY = 1000 * 60 * 60 * 24;
      const NOTIFICATION_INTERVAL = 2;
      const daysSinceLastShown =
        appUpdateNotificationShownTimestamp !== undefined
          ? Math.floor((Date.now() - appUpdateNotificationShownTimestamp) / MS_PER_DAY)
          : Number.MAX_VALUE;

      const showNotification = newVersionAvailable && daysSinceLastShown > NOTIFICATION_INTERVAL;

      if (showNotification) {
        listenerApi.dispatch(
          addNotification({
            type: 'info',
            title: 'A new version of the application is ready',
            description: 'Please refresh the page to get the latest update',
            actionCallbacks: ['reload'],
          }),
        );
        listenerApi.dispatch(appUpdateNotificationShown());
      }
    },
  });

  return middleware.middleware;
}
